"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const defaultFaqs = [
  {
    q: "Vilka typer av föremål kan ni hantera vid bortforsling?",
    a: "Vi kan forsla bort nästan allt – från gamla möbler, madrasser och vitvaror till kartonger, trädgårdsavfall och allmänt hushållsskräp. Vi tar även skrymmande föremål som soffor, bokhyllor och träningsutrustning. Oavsett om det gäller en garageröjning, kontorsröjning eller en hel lägenhetsröjning gör våra ungdomar bortforslingen snabb och smidig. Av säkerhetsskäl kan vi inte hantera farligt avfall som kemikalier, färg eller asbest.",
  },
  {
    q: "Kan ni bortforsla saker direkt från mitt hem (inomhus), eller måste de stå utomhus?",
    a: "Ja, våra ungdomar hämtar dina saker var de än står – inne i bostaden, på vinden, i källaren eller i garaget. Du behöver inte bära ut något själv. Vi är vana vid trånga trapphus, smala hissar och svåråtkomliga utrymmen.",
  },
  {
    q: "Vad händer med mina saker efter bortforslingen?",
    a: "Vi sorterar allt vi forslar bort. Det som fortfarande är i bra skick skänks vidare till organisationer som Myrorna eller Stadsmissionen. Resten lämnas på godkänd återvinningsstation där materialet sorteras och återvinns enligt gällande miljöregler. Vårt mål är alltid att så lite som möjligt hamnar på deponi.",
  },
  {
    q: "Kan ni bortforsla madrasser, elektronik eller vitvaror?",
    a: "Absolut. Vi hämtar och forslar bort madrasser, TV-apparater, datorer, tvättmaskiner, kylskåp, frysar och andra vitvaror. Elektronik och vitvaror sorteras ut separat och lämnas till godkänd elektronikåtervinning. Eventuella avfallsavgifter på återvinningsstationen kan tillkomma beroende på föremålstyp.",
  },
  {
    q: "Hur ska jag förbereda mig inför min bortforsling?",
    a: "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen kan du rensa en fri väg (ca 1 meter bred) från entrén till föremålen och om möjligt samla sakerna på ett ställe. Det minskar arbetstiden och håller nere kostnaden.",
  },
  {
    q: "Erbjuder ni bortforsling samma dag eller nästa dag?",
    a: "Ja, vi erbjuder både samdags- och nästadagsservice. Du kan boka våra ungdomar med så kort varsel som 30 minuter eller planera upp till 30 dagar i förväg. Välj helt enkelt den tid som passar dig bäst i bokningsformuläret.",
  },
  {
    q: "Kan ni montera isär möbler vid bortforsling?",
    a: "Ja, våra ungdomar hjälper gärna till med enklare isärmontering i samband med din bortforsling. De har med sig standardverktyg, men om dina möbler kräver specialverktyg eller instruktioner ber vi dig ha dessa redo. Tid för isärmontering ingår i din totala arbetstid.",
  },
  {
    q: "Kan ni skänka mina saker istället för att bortforsla dem?",
    a: "Ja! Om dina saker fortfarande är i bra skick kan vi leverera dem till en secondhand-butik eller välgörenhetsorganisation istället för att köra dem till tippen. Ange bara önskad inlämningsplats vid bokning. Vi rekommenderar att du kontaktar mottagaren i förväg för att bekräfta att de kan ta emot dina saker. Transport till återanvändning kan dessutom ge dig rätt till RUT-avdrag (25 %).",
  },
  {
    q: "Ingår avfallsavgifter i priset för min bortforsling?",
    a: "Nej, avfalls- och deponiavgifter ingår inte i priset utan debiteras separat. Avgiften varierar beroende på återvinningsstation samt vikten och typen av föremål, men ligger oftast runt 400 kr. Du ser alltid den totala kostnaden innan vi påbörjar uppdraget, så att du vet exakt vad det kostar.",
  },
  {
    q: "Kan jag få RUT-avdrag på bortforsling?",
    a: "Det beror på vad som händer med sakerna. Om dina föremål transporteras till en secondhand-butik, välgörenhetsorganisation eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där sakerna körs direkt till återvinningsstation eller deponi ger däremot inte rätt till RUT-avdrag enligt Skatteverkets regler. Vi hjälper dig gärna att avgöra vad som gäller för just ditt uppdrag – ange gärna vid bokning om du vill att sakerna ska skänkas så planerar vi upplägget därefter. Avdraget hanteras direkt på fakturan, så du behöver aldrig ligga ute med pengarna.",
  },
  {
    q: "Vad kostar bortforsling?",
    a: "Priset beror på mängden, typen av föremål och hur lång tid uppdraget tar. Du får ett pris direkt i vår bokning baserat på vad du anger – inga dolda avgifter eller överraskningar i efterhand. Avfallsavgifter på återvinningsstationen tillkommer separat och ligger oftast runt 400 kr. Vill du ha en exakt uppskattning för ett större uppdrag? Kontakta oss så återkommer vi med en skräddarsydd offert.",
  },
  {
    q: "Hur lång tid tar en bortforsling?",
    a: "Det varierar beroende på mängden och hur tillgängliga föremålen är. En enklare bortforsling av ett par möbler tar vanligtvis 30–60 minuter. Större uppdrag som en lägenhetsröjning eller dödsbotömning kan ta en halv till en hel dag. Våra ungdomar jobbar effektivt och är vana vid att bära tungt – vi ser till att det går så snabbt som möjligt utan att tumma på kvaliteten.",
  },
  {
    q: "Kan ni hjälpa med dödsbo eller lägenhetsröjning?",
    a: "Ja, vi har gedigen erfarenhet av att tömma dödsbon och hela lägenheter. Våra ungdomar hanterar allt från möbler och personliga tillhörigheter till grovsopor och elektronik. Vi sorterar noggrant – det som fortfarande är i bra skick skänks vidare och resten lämnas på godkänd återvinningsstation. Du behöver inte vara på plats om du inte vill, vi sköter hela processen åt dig.",
  },
  {
    q: "Vilka områden erbjuder ni bortforsling i?",
    a: "Vi erbjuder bortforsling i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö. Hittar du inte ditt område? Hör av dig så berättar vi om vi kan hjälpa dig.",
  },
  {
    q: "Vem ordnar parkering för lastbilen?",
    a: "Vi tar hand om det. Våra ungdomar är vana vid att hitta lösningar för parkering i trånga bostadsområden och innerstadsgator. Om det krävs parkeringstillstånd eller särskilt tillträde meddelar vi dig i förväg. Du behöver inte tänka på logistiken – det ingår i tjänsten.",
  },
];

interface ServiceFAQProps {
  heading?: string;
  faqs?: { q: string; a: string }[];
}

export default function ServiceFAQ({
  heading = "Frågor och svar - Bortforsling",
  faqs = defaultFaqs,
}: ServiceFAQProps = {}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const leftColumn = faqs.filter((_, i) => i % 2 === 0);
  const rightColumn = faqs.filter((_, i) => i % 2 === 1);

  const renderItem = (faq: { q: string; a: string }, originalIndex: number) => (
    <motion.div
      key={originalIndex}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3 }}
      className="border-b border-gray-200"
    >
      <button
        onClick={() =>
          setOpenIndex(openIndex === originalIndex ? null : originalIndex)
        }
        className="w-full flex items-start justify-between gap-4 py-5 text-left text-brand-dark hover:text-brand-secondary transition-colors"
      >
        <span className="text-sm font-medium leading-snug">
          {faq.q}
        </span>
        <motion.div
          animate={{ rotate: openIndex === originalIndex ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="shrink-0 mt-0.5"
        >
          <Plus className="h-4 w-4 text-gray-400" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {openIndex === originalIndex && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-sm text-gray-500 leading-relaxed">
              {faq.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  return (
    <section className="py-8 sm:pt-10 sm:pb-24 bg-white">
      <div className="mx-auto max-w-screen-2xl px-6 xl:px-14">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-xl sm:text-2xl font-bold text-brand-dark mb-10 sm:mb-14"
        >
          {heading}
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16">
          <div>{leftColumn.map((faq, i) => renderItem(faq, i * 2))}</div>
          <div>
            {rightColumn.map((faq, i) => renderItem(faq, i * 2 + 1))}
          </div>
        </div>
      </div>
    </section>
  );
}
